// CRB3 (docs/build-briefs/CRB3-custom-report-sales-datasets-charts.md; design 2.2 `output.chart` and section 3;
// design/mockups/report-builder/03a and 02b): the pure model of a custom report's chart. No DOM and nothing node-only, so a plain
// node:test file imports it (test/crb3-chart-model.test.ts) and the browser bundle does too.
//
// A chart is a rendering of the run's own result, never a second query: this reads the result's rows and columns and the chart
// slot of the spec it ran, and says what to draw (categories, series, per-series totals, axis ticks) or that there is nothing
// to draw, which the screen shows as "N/A" with the table still under it. Nothing here decides what a person may see.

import type { CustomReportCell, CustomReportColumnDto, CustomReportResultDto } from "./api.ts";
import { formatCount, formatMoney } from "./reportFormat.ts";

export type ChartType = "bar" | "line" | "stacked_bar" | "pie" | "number";

export interface ChartSpecDto {
  type: ChartType;
  x: string | null;
  y: string[];
  series: string | null;
}

/** The most a chart will draw; past these it reads N/A and the table carries the report. */
export const CHART_LIMITS = { barCategories: 40, lineCategories: 120, pieSlices: 8, series: 6 } as const;

/** RPTLAYOUT1-F1-S1: how a canned result is drawn (a line when the server asks for one, columns otherwise) and the most categories that drawing holds. */
export function drawingFor(requested: unknown): { type: ChartType; limit: number } {
  if (requested === "line") return { type: "line", limit: CHART_LIMITS.lineCategories };
  return { type: "bar", limit: CHART_LIMITS.barCategories };
}

export type ChartUnit = "money" | "number" | "percent" | "decimal";

export interface ChartSeries {
  key: string;
  label: string;
  /** One value per category, null where the report has no figure (never drawn as zero). */
  values: Array<number | null>;
  /** The sum of the values that exist. */
  total: number;
}

export interface ChartModel {
  type: ChartType;
  title: string;
  unit: ChartUnit;
  categories: string[];
  series: ChartSeries[];
  /** The sum of every series total. */
  total: number;
  /** The axis the locations and lines are drawn on: zero is always inside it. */
  axis: { min: number; max: number; ticks: number[] };
  /** A pie: each slice's share of the whole, in basis points, half up; they add to 10000. */
  shares: number[];
}

/** Nothing to draw. The screen says "N/A". */
export type ChartResult = { ok: true; model: ChartModel } | { ok: false };

const NOT_AVAILABLE = { ok: false } as const;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The chart slot of a result's spec, or undefined when the report has none or it is not one this build knows. */
export function chartOf(spec: unknown): ChartSpecDto | undefined {
  if (!isObject(spec) || !isObject(spec["output"])) return undefined;
  const raw = spec["output"]["chart"];
  if (!isObject(raw)) return undefined;
  const type = raw["type"];
  if (type !== "bar" && type !== "line" && type !== "stacked_bar" && type !== "pie" && type !== "number") return undefined;
  const y = raw["y"];
  if (!Array.isArray(y) || y.length === 0 || !y.every((entry) => typeof entry === "string")) return undefined;
  const x = typeof raw["x"] === "string" ? raw["x"] : null;
  const series = typeof raw["series"] === "string" ? raw["series"] : null;
  return { type, x, y: y as string[], series };
}

/** A figure for the chart: a whole number, or a decimal text, or nothing. */
function figure(cell: CustomReportCell | undefined): number | null {
  if (cell === null || cell === undefined || typeof cell === "boolean") return null;
  const parsed = typeof cell === "number" ? cell : Number(cell);
  return Number.isFinite(parsed) ? parsed : null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function hourText(hour: number): string {
  const suffix = hour < 12 ? "AM" : "PM";
  return `${hour % 12 === 0 ? 12 : hour % 12} ${suffix}`;
}

/** What a category or a series is called on the chart: a date as "Feb 10", an hour as "8 PM", an enum in its own words. */
export function categoryText(
  column: CustomReportColumnDto,
  cell: CustomReportCell,
  valueLabels?: Readonly<Record<string, string>>,
): string {
  if (cell === null || cell === undefined) return "-";
  if (column.type === "date" && typeof cell === "string") {
    const [, m, d] = cell.split("-").map(Number) as [number, number, number];
    return `${MONTHS[m - 1] ?? ""} ${d}`;
  }
  if (column.field === "hour_of_day" && typeof cell === "number") return hourText(cell);
  if (column.type === "boolean") return cell === true ? "Yes" : "No";
  if (typeof cell === "string") return valueLabels?.[cell] ?? cell;
  return String(cell);
}

/** A figure on an axis or a label, short: $7.5k, $250, 1.2k, 42, 12.5%. */
export function compactValue(value: number, unit: ChartUnit): string {
  if (unit === "percent") return `${(value / 100).toFixed(value % 100 === 0 ? 0 : 1)}%`;
  const base = unit === "money" ? value / 100 : value;
  const magnitude = Math.abs(base);
  const sign = base < 0 ? "-" : "";
  const prefix = unit === "money" ? "$" : "";
  if (magnitude >= 1_000_000) return `${sign}${prefix}${trim(magnitude / 1_000_000)}M`;
  if (magnitude >= 1000) return `${sign}${prefix}${trim(magnitude / 1000)}k`;
  if (unit === "money" && magnitude % 1 !== 0) return `${sign}${prefix}${magnitude.toFixed(2)}`;
  return `${sign}${prefix}${trim(magnitude)}`;
}

function trim(value: number): string {
  const text = value >= 100 ? value.toFixed(0) : value.toFixed(1);
  return text.replace(/\.0$/, "");
}

/** The exact figure, as the table shows it. */
export function exactValue(value: number, unit: ChartUnit): string {
  if (unit === "money") return formatMoney(value);
  if (unit === "percent") return `${(value / 100).toFixed(1)}%`;
  if (unit === "decimal") return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return formatCount(value);
}

/** The ticks of an axis that holds zero and every value: a step of 1, 2, 2.5 or 5 times a power of ten, about four of them. */
export function niceAxis(low: number, high: number): { min: number; max: number; ticks: number[] } {
  const lo = Math.min(0, low);
  const hi = Math.max(0, high);
  const span = hi - lo;
  if (span === 0) return { min: 0, max: 1, ticks: [0, 1] };
  const rough = span / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const fraction = rough / power;
  const step = (fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10) * power;
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let i = 0; min + i * step <= max + step / 1000; i += 1) ticks.push(Math.round((min + i * step) * 1e6) / 1e6);
  return { min, max, ticks };
}

/** Shares in basis points that add to 10000, the remainder going to the largest slices (largest remainder method). */
export function sharesInBasisPoints(values: readonly number[]): number[] {
  const total = values.reduce((t, v) => t + v, 0);
  if (total <= 0) return values.map(() => 0);
  const exact = values.map((v) => (v * 10_000) / total);
  const floors = exact.map(Math.floor);
  let left = 10_000 - floors.reduce((t, v) => t + v, 0);
  const order = exact
    .map((value, index) => ({ index, remainder: value - Math.floor(value) }))
    .sort((a, b) => b.remainder - a.remainder || a.index - b.index);
  for (const entry of order) {
    if (left <= 0) break;
    floors[entry.index] = (floors[entry.index] as number) + 1;
    left -= 1;
  }
  return floors;
}

function unitOf(column: CustomReportColumnDto): ChartUnit | undefined {
  switch (column.type) {
    case "money":
      return "money";
    case "number":
      return "number";
    case "percent":
      return "percent";
    case "decimal":
      return "decimal";
    default:
      return undefined;
  }
}

/**
 * The chart of a result, from the chart slot of the spec it ran. `valueLabels` names the words of an enum column by its field
 * (the catalog's). Anything that cannot be drawn faithfully is N/A: no rows, no figure at all, a pie with a value below zero, a
 * stacked bar with one, or more categories or series than the chart can show.
 */
export function buildChartModel(
  result: CustomReportResultDto,
  chart: ChartSpecDto,
  valueLabels: Readonly<Record<string, Readonly<Record<string, string>>>> = {},
): ChartResult {
  const columnAt = (key: string): number => result.columns.findIndex((c) => c.key === key);
  const yIndexes = chart.y.map(columnAt);
  if (yIndexes.some((i) => i < 0)) return NOT_AVAILABLE;
  const yColumns = yIndexes.map((i) => result.columns[i] as CustomReportColumnDto);
  const units = new Set(yColumns.map(unitOf));
  const unit = unitOf(yColumns[0] as CustomReportColumnDto);
  if (unit === undefined || units.size !== 1) return NOT_AVAILABLE;
  if (result.rows.length === 0) return NOT_AVAILABLE;

  const title = chartTitle(result, chart);
  const labelsFor = (column: CustomReportColumnDto) => valueLabels[column.field];

  if (chart.type === "number") {
    const value = figure(result.rows[0]?.[yIndexes[0] as number]);
    if (value === null) return NOT_AVAILABLE;
    return {
      ok: true,
      model: {
        type: "number",
        title,
        unit,
        categories: [],
        series: [{ key: chart.y[0] as string, label: (yColumns[0] as CustomReportColumnDto).label, values: [value], total: value }],
        total: value,
        axis: niceAxis(value, value),
        shares: [],
      },
    };
  }

  const xIndex = chart.x === null ? -1 : columnAt(chart.x);
  if (xIndex < 0) return NOT_AVAILABLE;
  const xColumn = result.columns[xIndex] as CustomReportColumnDto;
  const seriesIndex = chart.series === null ? -1 : columnAt(chart.series);
  if (chart.series !== null && seriesIndex < 0) return NOT_AVAILABLE;

  // Categories in the order the rows came, one per distinct x.
  const categoryKeys: string[] = [];
  const categoryCells: CustomReportCell[] = [];
  const categoryAt = new Map<string, number>();
  const seriesKeys: string[] = [];
  const seriesCells: CustomReportCell[] = [];
  const seriesAt = new Map<string, number>();
  for (const row of result.rows) {
    const x = JSON.stringify(row[xIndex] ?? null);
    if (!categoryAt.has(x)) {
      categoryAt.set(x, categoryKeys.length);
      categoryKeys.push(x);
      categoryCells.push(row[xIndex] ?? null);
    }
    if (seriesIndex >= 0) {
      const s = JSON.stringify(row[seriesIndex] ?? null);
      if (!seriesAt.has(s)) {
        seriesAt.set(s, seriesKeys.length);
        seriesKeys.push(s);
        seriesCells.push(row[seriesIndex] ?? null);
      }
    }
  }

  const series: ChartSeries[] = [];
  if (seriesIndex >= 0) {
    const yIndex = yIndexes[0] as number;
    const seriesColumn = result.columns[seriesIndex] as CustomReportColumnDto;
    seriesKeys.forEach((key, at) => {
      series.push({
        key,
        label: categoryText(seriesColumn, seriesCells[at] ?? null, labelsFor(seriesColumn)),
        values: categoryKeys.map(() => null),
        total: 0,
      });
    });
    for (const row of result.rows) {
      const c = categoryAt.get(JSON.stringify(row[xIndex] ?? null)) as number;
      const s = series[seriesAt.get(JSON.stringify(row[seriesIndex] ?? null)) as number] as ChartSeries;
      const value = figure(row[yIndex]);
      if (value !== null) s.values[c] = (s.values[c] ?? 0) + value;
    }
  } else {
    yIndexes.forEach((yIndex, at) => {
      const column = yColumns[at] as CustomReportColumnDto;
      const values: Array<number | null> = categoryKeys.map(() => null);
      for (const row of result.rows) {
        const c = categoryAt.get(JSON.stringify(row[xIndex] ?? null)) as number;
        const value = figure(row[yIndex]);
        if (value !== null) values[c] = (values[c] ?? 0) + value;
      }
      series.push({ key: column.key, label: column.label, values, total: 0 });
    });
  }
  for (const s of series) s.total = s.values.reduce<number>((t, v) => t + (v ?? 0), 0);

  const everyValue = series.flatMap((s) => s.values).filter((v): v is number => v !== null);
  if (everyValue.length === 0) return NOT_AVAILABLE;
  if (series.length > CHART_LIMITS.series) return NOT_AVAILABLE;
  const categories = categoryCells.map((cell) => categoryText(xColumn, cell, labelsFor(xColumn)));
  const total = series.reduce((t, s) => t + s.total, 0);

  if (chart.type === "pie") {
    const only = series[0] as ChartSeries;
    if (series.length !== 1 || categories.length > CHART_LIMITS.pieSlices) return NOT_AVAILABLE;
    if (only.values.some((v) => v !== null && v < 0) || total <= 0) return NOT_AVAILABLE;
    const values = only.values.map((v) => v ?? 0);
    return { ok: true, model: { type: "pie", title, unit, categories, series, total, axis: niceAxis(0, total), shares: sharesInBasisPoints(values) } };
  }
  if (categories.length > drawingFor(chart.type).limit) return NOT_AVAILABLE;
  if (chart.type === "stacked_bar" && everyValue.some((v) => v < 0)) return NOT_AVAILABLE;
  let low = Math.min(...everyValue);
  let high = Math.max(...everyValue);
  if (chart.type === "stacked_bar") {
    const stackTotals = categories.map((_, c) => series.reduce((t, s) => t + (s.values[c] ?? 0), 0));
    low = 0;
    high = Math.max(...stackTotals);
  }
  return { ok: true, model: { type: chart.type, title, unit, categories, series, total, axis: niceAxis(low, high), shares: [] } };
}

/** "Net sales by Business date": the measure or measures, then what they are grouped by. */
export function chartTitle(result: CustomReportResultDto, chart: ChartSpecDto): string {
  const label = (key: string): string => result.columns.find((c) => c.key === key)?.label ?? key;
  const measures = chart.series === null ? chart.y.map(label).join(", ") : label(chart.y[0] as string);
  if (chart.type === "number") return measures;
  return chart.x === null ? measures : `${measures} by ${label(chart.x).toLowerCase()}`;
}

/** The chart in words, for the person who cannot see it: the title, then each category with its figures. */
export function chartDescription(model: ChartModel): string {
  if (model.type === "number") return `${model.title}: ${exactValue(model.total, model.unit)}`;
  const parts = model.categories.map((category, c) => {
    const figures = model.series
      .map((s) => `${model.series.length > 1 ? `${s.label} ` : ""}${s.values[c] === null || s.values[c] === undefined ? "-" : exactValue(s.values[c] as number, model.unit)}`)
      .join(", ");
    return `${category} ${figures}`;
  });
  return `${model.title}. ${parts.join("; ")}.`;
}
